import { useFormContext, useWatch } from 'react-hook-form';
import FormCheckbox from 'components/common/forms/FormCheckbox';
import FormAutoHeightTextarea from 'components/common/forms/FormAutoHeightTextarea';
import styles from 'styles/pages/chart/PreventiveDentalTreatmentChart.module.scss';
import {
  PreventiveDentalTreatmentChartFields,
  Treatment1ActionLabel,
  Treatment1PlanLabel,
  Treatment2ActionLabel,
  Treatment2PlanLabel,
  TreatmentType,
} from 'constants/chart/preventiveDentalTreatmentChart';
import classNames from 'classnames';
import useModal from 'hooks/useModal';

type TProps = {
  category: string;
  isRegister: boolean;
  isReadOnly: boolean;
  activeTabIndex: number;
};

const PreventiveDentalTreatmentChart = ({
  category,
  isRegister,
  isReadOnly,
  activeTabIndex,
}: TProps) => {
  const { openModal } = useModal();

  const { setValue, control } = useFormContext();

  const treatment1PlanWatch = useWatch({
    name: PreventiveDentalTreatmentChartFields.Treatment1Plan,
    control,
  });
  const treatment1PlanDetailWatch = useWatch({
    name: PreventiveDentalTreatmentChartFields.Treatment1PlanDetail,
    control,
  });

  const treatment1ActionWatch = useWatch({
    name: PreventiveDentalTreatmentChartFields.Treatment1Action,
    control,
  });
  const treatment1ActionDetailWatch = useWatch({
    name: PreventiveDentalTreatmentChartFields.Treatment1ActionDetail,
    control,
  });

  const treatment2PlanWatch = useWatch({
    name: PreventiveDentalTreatmentChartFields.Treatment2Plan,
    control,
  });
  const treatment2PlanDetailWatch = useWatch({
    name: PreventiveDentalTreatmentChartFields.Treatment2PlanDetail,
    control,
  });

  const treatment2ActionWatch = useWatch({
    name: PreventiveDentalTreatmentChartFields.Treatment2Action,
    control,
  });
  const treatment2ActionDetailWatch = useWatch({
    name: PreventiveDentalTreatmentChartFields.Treatment2ActionDetail,
    control,
  });

  return (
    <>
      <table className={styles.table}>
        <colgroup>
          <col style={{ width: '64px' }} />
          <col style={{ width: '396px' }} />
          <col style={{ width: '64px' }} />
          <col style={{ width: '396px' }} />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.th} colSpan={2}>
              1차
            </th>
            <th className={styles.th} colSpan={2}>
              2차
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={styles.td_title_border_bottom}>
              <span>계획</span>
            </td>
            <td className={styles.td_border_bottom}>
              <span>
                <div>
                  <div className={styles.checkbox_wrapper}>
                    <FormCheckbox
                      name={PreventiveDentalTreatmentChartFields.Treatment1Plan}
                      text={
                        Treatment1PlanLabel[TreatmentType.일차_계획_불소도포]
                      }
                      value={TreatmentType.일차_계획_불소도포}
                      multiple={true}
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        (category !== 'practice' && activeTabIndex === 2)
                      }
                    />
                    <FormCheckbox
                      name={PreventiveDentalTreatmentChartFields.Treatment1Plan}
                      text={
                        Treatment1PlanLabel[TreatmentType.일차_계획_구취측정]
                      }
                      value={TreatmentType.일차_계획_구취측정}
                      multiple={true}
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        (category !== 'practice' && activeTabIndex === 2)
                      }
                    />
                    <FormCheckbox
                      name={PreventiveDentalTreatmentChartFields.Treatment1Plan}
                      text={
                        Treatment1PlanLabel[TreatmentType.일차_계획_TBI교육]
                      }
                      value={TreatmentType.일차_계획_TBI교육}
                      multiple={true}
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        (category !== 'practice' && activeTabIndex === 2)
                      }
                    />
                  </div>
                  <div className={styles.etc_wrapper}>
                    <FormCheckbox
                      name={PreventiveDentalTreatmentChartFields.Treatment1Plan}
                      text={Treatment1PlanLabel[TreatmentType.일차_계획_기타]}
                      labelStyle={{ paddingRight: '0' }}
                      value={TreatmentType.일차_계획_기타}
                      multiple={true}
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        (category !== 'practice' && activeTabIndex === 2)
                      }
                      afterChange={() => {
                        if (
                          treatment1PlanWatch?.includes(
                            TreatmentType.일차_계획_기타
                          ) &&
                          treatment1PlanDetailWatch
                        ) {
                          openModal({
                            isConfirm: true,
                            title: '작성 중인 내용을 삭제하시겠습니까?',
                            text: '작성 중인 내용이 삭제됩니다.',
                            onCancel: () => {
                              setValue(
                                PreventiveDentalTreatmentChartFields.Treatment1Plan,
                                [
                                  ...treatment1PlanWatch,
                                  TreatmentType.일차_계획_기타,
                                ]
                              );
                            },
                            onSubmit: () => {
                              setValue(
                                PreventiveDentalTreatmentChartFields.Treatment1PlanDetail,
                                ''
                              );
                            },
                          });
                        }
                      }}
                    />
                    <FormAutoHeightTextarea
                      name={
                        PreventiveDentalTreatmentChartFields.Treatment1PlanDetail
                      }
                      textareaStyle={{ width: '245px' }}
                      placeholder="최대 15자 입력 가능"
                      maxLength={15}
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        (category !== 'practice' && activeTabIndex === 2) ||
                        !treatment1PlanWatch.includes(
                          TreatmentType.일차_계획_기타
                        )
                      }
                    />
                  </div>
                </div>
              </span>
            </td>
            <td className={styles.td_title_border_bottom_left}>
              <span>계획</span>
            </td>
            <td className={styles.td_border_bottom}>
              <span>
                <div className={styles.checkbox_wrapper}>
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Plan}
                    text={
                      Treatment2PlanLabel[TreatmentType.이차_계획_치면열구전색]
                    }
                    value={TreatmentType.이차_계획_치면열구전색}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Plan}
                    text={
                      Treatment2PlanLabel[
                        TreatmentType.이차_계획_와타나베칫솔질
                      ]
                    }
                    value={TreatmentType.이차_계획_와타나베칫솔질}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Plan}
                    text={Treatment2PlanLabel[TreatmentType.이차_계획_치면연마]}
                    value={TreatmentType.이차_계획_치면연마}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                </div>
                <div
                  className={classNames(styles.checkbox_wrapper, styles.margin)}
                >
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Plan}
                    text={Treatment2PlanLabel[TreatmentType.이차_계획_구취측정]}
                    value={TreatmentType.이차_계획_구취측정}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Plan}
                    text={Treatment2PlanLabel[TreatmentType.이차_계획_불소도포]}
                    value={TreatmentType.이차_계획_불소도포}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Plan}
                    text={Treatment2PlanLabel[TreatmentType.이차_계획_TBI교육]}
                    value={TreatmentType.이차_계획_TBI교육}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                </div>
                <div className={styles.etc_wrapper}>
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Plan}
                    text={Treatment2PlanLabel[TreatmentType.이차_계획_기타]}
                    value={TreatmentType.이차_계획_기타}
                    labelStyle={{ paddingRight: '0' }}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                    afterChange={() => {
                      if (
                        treatment2PlanWatch?.includes(
                          TreatmentType.이차_계획_기타
                        ) &&
                        treatment2PlanDetailWatch
                      ) {
                        openModal({
                          isConfirm: true,
                          title: '작성 중인 내용을 삭제하시겠습니까?',
                          text: '작성 중인 내용이 삭제됩니다.',
                          onCancel: () => {
                            setValue(
                              PreventiveDentalTreatmentChartFields.Treatment2Plan,
                              [
                                ...treatment2PlanWatch,
                                TreatmentType.이차_계획_기타,
                              ]
                            );
                          },
                          onSubmit: () => {
                            setValue(
                              PreventiveDentalTreatmentChartFields.Treatment2PlanDetail,
                              ''
                            );
                          },
                        });
                      }
                    }}
                  />
                  <FormAutoHeightTextarea
                    name={
                      PreventiveDentalTreatmentChartFields.Treatment2PlanDetail
                    }
                    textareaStyle={{ width: '245px' }}
                    placeholder="최대 15자 입력 가능"
                    maxLength={15}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0) ||
                      !treatment2PlanWatch.includes(
                        TreatmentType.이차_계획_기타
                      )
                    }
                  />
                </div>
              </span>
            </td>
          </tr>
          <tr>
            <td className={styles.td_title}>
              <span>수행</span>
            </td>
            <td className={styles.td_layout}>
              <span>
                <div className={styles.checkbox_wrapper}>
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment1Action}
                    text={
                      Treatment1ActionLabel[TreatmentType.일차_수행_불소도포]
                    }
                    value={TreatmentType.일차_수행_불소도포}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 2)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment1Action}
                    text={
                      Treatment1ActionLabel[TreatmentType.일차_수행_구취측정]
                    }
                    value={TreatmentType.일차_수행_구취측정}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 2)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment1Action}
                    text={
                      Treatment1ActionLabel[TreatmentType.일차_수행_TBI교육]
                    }
                    value={TreatmentType.일차_수행_TBI교육}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 2)
                    }
                  />
                </div>
                <div className={styles.etc_wrapper}>
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment1Action}
                    text={Treatment1ActionLabel[TreatmentType.일차_수행_기타]}
                    value={TreatmentType.일차_수행_기타}
                    labelStyle={{ paddingRight: '0' }}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 2)
                    }
                    afterChange={() => {
                      if (
                        treatment1ActionWatch?.includes(
                          TreatmentType.일차_수행_기타
                        ) &&
                        treatment1ActionDetailWatch
                      ) {
                        openModal({
                          isConfirm: true,
                          title: '작성 중인 내용을 삭제하시겠습니까?',
                          text: '작성 중인 내용이 삭제됩니다.',
                          onCancel: () => {
                            setValue(
                              PreventiveDentalTreatmentChartFields.Treatment1Action,
                              [
                                ...treatment1ActionWatch,
                                TreatmentType.일차_수행_기타,
                              ]
                            );
                          },
                          onSubmit: () => {
                            setValue(
                              PreventiveDentalTreatmentChartFields.Treatment1ActionDetail,
                              ''
                            );
                          },
                        });
                      }
                    }}
                  />
                  <FormAutoHeightTextarea
                    name={
                      PreventiveDentalTreatmentChartFields.Treatment1ActionDetail
                    }
                    textareaStyle={{ width: '245px' }}
                    placeholder="최대 15자 입력 가능"
                    maxLength={15}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 2) ||
                      !treatment1ActionWatch.includes(
                        TreatmentType.일차_수행_기타
                      )
                    }
                  />
                </div>
              </span>
            </td>
            <td className={styles.td_title_border_left}>
              <span>수행</span>
            </td>
            <td className={styles.td_layout}>
              <span>
                <div className={styles.checkbox_wrapper}>
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Action}
                    text={
                      Treatment2ActionLabel[
                        TreatmentType.이차_수행_치면열구전색
                      ]
                    }
                    value={TreatmentType.이차_수행_치면열구전색}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Action}
                    text={
                      Treatment2ActionLabel[
                        TreatmentType.이차_수행_와타나베칫솔질
                      ]
                    }
                    value={TreatmentType.이차_수행_와타나베칫솔질}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Action}
                    text={
                      Treatment2ActionLabel[TreatmentType.이차_수행_치면연마]
                    }
                    value={TreatmentType.이차_수행_치면연마}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                </div>
                <div
                  className={classNames(styles.checkbox_wrapper, styles.margin)}
                >
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Action}
                    text={
                      Treatment2ActionLabel[TreatmentType.이차_수행_구취측정]
                    }
                    value={TreatmentType.이차_수행_구취측정}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Action}
                    text={
                      Treatment2ActionLabel[TreatmentType.이차_수행_불소도포]
                    }
                    value={TreatmentType.이차_수행_불소도포}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Action}
                    text={
                      Treatment2ActionLabel[TreatmentType.이차_수행_TBI교육]
                    }
                    value={TreatmentType.이차_수행_TBI교육}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                  />
                </div>
                <div className={styles.etc_wrapper}>
                  <FormCheckbox
                    name={PreventiveDentalTreatmentChartFields.Treatment2Action}
                    text={Treatment2ActionLabel[TreatmentType.이차_수행_기타]}
                    value={TreatmentType.이차_수행_기타}
                    multiple={true}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0)
                    }
                    afterChange={() => {
                      if (
                        treatment2ActionWatch?.includes(
                          TreatmentType.이차_수행_기타
                        ) &&
                        treatment2ActionDetailWatch
                      ) {
                        openModal({
                          isConfirm: true,
                          title: '작성 중인 내용을 삭제하시겠습니까?',
                          text: '작성 중인 내용이 삭제됩니다.',
                          onCancel: () => {
                            setValue(
                              PreventiveDentalTreatmentChartFields.Treatment2Action,
                              [
                                ...treatment2ActionWatch,
                                TreatmentType.이차_수행_기타,
                              ]
                            );
                          },
                          onSubmit: () => {
                            setValue(
                              PreventiveDentalTreatmentChartFields.Treatment2ActionDetail,
                              ''
                            );
                          },
                        });
                      }
                    }}
                  />
                  <FormAutoHeightTextarea
                    name={
                      PreventiveDentalTreatmentChartFields.Treatment2ActionDetail
                    }
                    textareaStyle={{ width: '245px' }}
                    placeholder="최대 15자 입력 가능"
                    maxLength={15}
                    readOnly={
                      !isRegister ||
                      isReadOnly ||
                      (category !== 'practice' && activeTabIndex === 0) ||
                      !treatment2ActionWatch.includes(
                        TreatmentType.이차_수행_기타
                      )
                    }
                  />
                </div>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default PreventiveDentalTreatmentChart;
